import { useState, useMemo, useCallback, useEffect, useRef } from 'react';

const BRAND = '#F96B25';
const DARK_NAVY = '#0c1527';

interface CalcState {
  employees: number;
  hiresPerMonth: number;
  hrCount: number;
  avgHrSalary: number;
  avgWorkerSalary: number;
  daysToClose: number;
  hotVacancyPct: number;
}

const CLAMP = (v: number, lo: number, hi: number) => Math.min(Math.max(v, lo), hi);

function fmtUa(n: number): string {
  return new Intl.NumberFormat('uk-UA', { maximumFractionDigits: 0 }).format(n);
}

function fmtMln(n: number): string {
  return (n / 1_000_000).toFixed(1).replace('.', ',') + ' млн';
}

function fmtMoney(min: number, max: number): string {
  if (min >= 1_000_000 && max >= 1_000_000) {
    return `${fmtMln(min)}–${fmtMln(max)} грн`;
  }
  const rMin = Math.round(min / 10_000) * 10_000;
  const rMax = Math.round(max / 10_000) * 10_000;
  return `${fmtUa(rMin)}–${fmtUa(rMax)} грн`;
}

function fmtMoneySingle(n: number): string {
  if (n >= 1_000_000) return fmtMln(n);
  const r = Math.round(n / 10_000) * 10_000;
  return fmtUa(r) + ' грн';
}

function computeDefaults(employees: number) {
  const hiresDefault = CLAMP(Math.round((employees * 0.35) / 12), 1, 300);
  const hrDefault = CLAMP(Math.max(1, Math.round(employees / 125)), 1, 50);
  return { hiresDefault, hrDefault };
}

export default function CostOfDelayCalculator() {
  const defaults = computeDefaults(500);

  const [calc, setCalc] = useState<CalcState>({
    employees: 500,
    hiresPerMonth: defaults.hiresDefault,
    hrCount: defaults.hrDefault,
    avgHrSalary: 40000,
    avgWorkerSalary: 25000,
    daysToClose: 30,
    hotVacancyPct: 70,
  });

  const [hiresManual, setHiresManual] = useState(false);
  const [hrManual, setHrManual] = useState(false);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [methodologyOpen, setMethodologyOpen] = useState(false);

  const interactedRef = useRef(false);

  const markInteracted = useCallback(() => {
    if (!interactedRef.current) {
      interactedRef.current = true;
      (window as any).dataLayer?.push?.({ event: 'calc_interacted' });
    }
  }, []);

  const setValue = useCallback(
    (key: keyof CalcState) => (e: React.ChangeEvent<HTMLInputElement>) => {
      markInteracted();
      const raw = Number(e.target.value);
      if (key === 'employees') {
        const v = CLAMP(raw, 100, 10000);
        setCalc((prev) => {
          const d = computeDefaults(v);
          return {
            ...prev,
            employees: v,
            hiresPerMonth: hiresManual ? prev.hiresPerMonth : d.hiresDefault,
            hrCount: hrManual ? prev.hrCount : d.hrDefault,
          };
        });
      } else if (key === 'hiresPerMonth') {
        setCalc((prev) => ({ ...prev, hiresPerMonth: CLAMP(raw, 1, 300) }));
        setHiresManual(true);
      } else if (key === 'hrCount') {
        setCalc((prev) => ({ ...prev, hrCount: CLAMP(raw, 1, 50) }));
        setHrManual(true);
      } else {
        setCalc((prev) => ({ ...prev, [key]: raw }));
      }
    },
    [hiresManual, hrManual, markInteracted],
  );

  const stepValue = useCallback(
    (key: 'employees' | 'hiresPerMonth' | 'hrCount' | 'hotVacancyPct', delta: number) => () => {
      markInteracted();
      setCalc((prev) => {
        if (key === 'employees') {
          const v = CLAMP(prev.employees + delta * 50, 100, 10000);
          const d = computeDefaults(v);
          return {
            ...prev,
            employees: v,
            hiresPerMonth: hiresManual ? prev.hiresPerMonth : d.hiresDefault,
            hrCount: hrManual ? prev.hrCount : d.hrDefault,
          };
        }
        if (key === 'hiresPerMonth') {
          setHiresManual(true);
          return { ...prev, hiresPerMonth: CLAMP(prev.hiresPerMonth + delta, 1, 300) };
        }
        if (key === 'hrCount') {
          setHrManual(true);
          return { ...prev, hrCount: CLAMP(prev.hrCount + delta, 1, 50) };
        }
        return { ...prev, hotVacancyPct: CLAMP(prev.hotVacancyPct + delta * 5, 30, 100) };
      });
    },
    [hiresManual, hrManual, markInteracted],
  );

  const resetHires = useCallback(() => {
    setHiresManual(false);
    setCalc((prev) => {
      const d = computeDefaults(prev.employees);
      return { ...prev, hiresPerMonth: d.hiresDefault };
    });
  }, []);

  const resetHr = useCallback(() => {
    setHrManual(false);
    setCalc((prev) => {
      const d = computeDefaults(prev.employees);
      return { ...prev, hrCount: d.hrDefault };
    });
  }, []);

  useEffect(() => {
    setCalc((prev) => {
      const next = { ...prev };
      let changed = false;
      if (prev.avgHrSalary < 10000 || prev.avgHrSalary > 200000) {
        next.avgHrSalary = CLAMP(prev.avgHrSalary, 10000, 200000);
        changed = true;
      }
      if (prev.avgWorkerSalary < 8000 || prev.avgWorkerSalary > 150000) {
        next.avgWorkerSalary = CLAMP(prev.avgWorkerSalary, 8000, 150000);
        changed = true;
      }
      if (prev.daysToClose < 5 || prev.daysToClose > 120) {
        next.daysToClose = CLAMP(prev.daysToClose, 5, 120);
        changed = true;
      }
      return changed ? next : prev;
    });
  }, [calc.avgHrSalary, calc.avgWorkerSalary, calc.daysToClose]);

  const results = useMemo(() => {
    const hiresYear = calc.hiresPerMonth * 12;
    const daysSaved = Math.round(calc.daysToClose * (2 / 3));
    const dailyRate = calc.avgWorkerSalary / 22;
    const hardTypical = hiresYear * (calc.hotVacancyPct / 100) * daysSaved * dailyRate;
    const hardConservative = hiresYear * ((calc.hotVacancyPct / 100) * 0.7) * daysSaved * dailyRate;
    const soft = calc.hrCount * calc.avgHrSalary * 0.43 * 12;
    const hrHours = Math.round(calc.hrCount * 168 * 0.43);
    const totalMin = hardConservative + soft;
    const totalMax = hardTypical + soft;
    const fopEq = Math.max(1, Math.round(totalMax / (calc.avgWorkerSalary * 12)));

    return {
      hiresYear,
      daysSaved,
      hardTypical: Math.round(hardTypical),
      hardConservative: Math.round(hardConservative),
      soft: Math.round(soft),
      hrHours,
      totalMin: Math.round(totalMin),
      totalMax: Math.round(totalMax),
      fopEq,
    };
  }, [calc]);

  const hiresLabel = hiresManual
    ? `≈ плинність ${fmtUa(Math.round((calc.hiresPerMonth * 12 / calc.employees) * 100))}% на рік`
    : 'виходимо з плинності 35% — типової для виробництва. Посуньте, якщо у вас інакше';

  const hrLabel = hrManual
    ? `поточне співвідношення: 1 HR на ~${fmtUa(Math.round(calc.employees / calc.hrCount))} працівників`
    : 'орієнтир — 1 HR на ~125 працівників';

  const avgHrSalaryOutOfRange = calc.avgHrSalary < 10000 || calc.avgHrSalary > 200000;
  const avgWorkerSalaryOutOfRange = calc.avgWorkerSalary < 8000 || calc.avgWorkerSalary > 150000;
  const daysToCloseOutOfRange = calc.daysToClose < 5 || calc.daysToClose > 120;

  return (
    <section id="cost-calculator" className="py-24 lg:py-32 bg-background-100">
      <div className="max-w-[1100px] mx-auto px-6 lg:px-10">

        {/* Header */}
        <div className="text-center mb-16 lg:mb-20 max-w-[720px] mx-auto">
          <h2
            className="font-heading font-bold text-foreground-950 leading-[1.08] tracking-tight mb-5"
            style={{ fontSize: 'clamp(1.75rem, 4vw, 3rem)' }}
          >
            Скільки коштує повільний найм і відсутність автоматизації саме вам
          </h2>
          <p className="text-foreground-600 text-lg lg:text-xl leading-relaxed">
            Три кліки — і ви бачите ціну відсутності автоматизації. Розрахунок оцінковий, за консервативними формулами.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[1fr_480px] gap-6 lg:gap-10 items-start">

          {/* ── LEFT: Inputs ── */}
          <div className="bg-white rounded-[28px] border border-background-200/70 p-8 lg:p-10">
            <div className="flex flex-col gap-10">

              {/* Slider 1 */}
              <SliderRow
                id="calc-employees"
                label="Працівників на виробництві"
                value={calc.employees}
                min={100}
                max={10000}
                step={50}
                unit="осіб"
                onChange={setValue('employees')}
                onMinus={stepValue('employees', -1)}
                onPlus={stepValue('employees', 1)}
              />

              {/* Slider 2 */}
              <div>
                <SliderRow
                  id="calc-hires"
                  label="Наймаєте на місяць, людей"
                  value={calc.hiresPerMonth}
                  min={1}
                  max={300}
                  step={1}
                  unit="осіб"
                  onChange={setValue('hiresPerMonth')}
                  onMinus={stepValue('hiresPerMonth', -1)}
                  onPlus={stepValue('hiresPerMonth', 1)}
                />
                <div className="flex items-center gap-2 mt-2.5 flex-wrap">
                  <p className="text-foreground-500 text-sm leading-snug">
                    {hiresLabel}
                  </p>
                  {hiresManual && (
                    <button
                      onClick={resetHires}
                      className="text-sm cursor-pointer whitespace-nowrap hover:underline transition-all"
                      style={{ color: BRAND }}
                    >
                      повернути за формулою
                    </button>
                  )}
                </div>
              </div>

              {/* Slider 3 */}
              <div>
                <SliderRow
                  id="calc-hr"
                  label="Фахівців в HR та рекрутингу"
                  value={calc.hrCount}
                  min={1}
                  max={50}
                  step={1}
                  unit="осіб"
                  onChange={setValue('hrCount')}
                  onMinus={stepValue('hrCount', -1)}
                  onPlus={stepValue('hrCount', 1)}
                />
                <div className="flex items-center gap-2 mt-2.5 flex-wrap">
                  <p className="text-foreground-500 text-sm leading-snug">
                    {hrLabel}
                  </p>
                  {hrManual && (
                    <button
                      onClick={resetHr}
                      className="text-sm cursor-pointer whitespace-nowrap hover:underline transition-all"
                      style={{ color: BRAND }}
                    >
                      повернути за формулою
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Advanced panel */}
            <div className="mt-10 pt-8 border-t border-background-200/70">
              <button
                onClick={() => {
                  markInteracted();
                  setAdvancedOpen(!advancedOpen);
                }}
                className="w-full flex items-center justify-between gap-3 text-left cursor-pointer py-1 group"
              >
                <span className="text-foreground-700 text-base font-medium group-hover:text-foreground-900 transition-colors">
                  Уточнити розрахунок
                </span>
                <i
                  className={`ri-arrow-down-s-line text-lg text-foreground-400 transition-transform duration-200 ${advancedOpen ? 'rotate-180' : ''}`}
                />
              </button>

              {advancedOpen && (
                <div className="mt-6">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="calc-hr-salary" className="block text-foreground-700 text-sm mb-2 font-medium">
                        Середня ЗП HR-фахівця, грн/міс
                      </label>
                      <input
                        id="calc-hr-salary"
                        type="number"
                        min={10000}
                        max={200000}
                        value={calc.avgHrSalary}
                        onChange={setValue('avgHrSalary')}
                        className={`w-full rounded-xl border bg-white px-4 py-2.5 text-base text-foreground-950 outline-none transition-colors ${avgHrSalaryOutOfRange ? 'border-red-400 bg-red-50/40' : 'border-background-200 focus:border-foreground-400'}`}
                      />
                      {avgHrSalaryOutOfRange && (
                        <p className="text-red-500 text-xs mt-1.5">Значення поза межами (10 000 – 200 000), застосовано обмеження</p>
                      )}
                    </div>

                    <div>
                      <label htmlFor="calc-worker-salary" className="block text-foreground-700 text-sm mb-2 font-medium">
                        Середня ЗП лінійного працівника, грн/міс
                      </label>
                      <input
                        id="calc-worker-salary"
                        type="number"
                        min={8000}
                        max={150000}
                        value={calc.avgWorkerSalary}
                        onChange={setValue('avgWorkerSalary')}
                        className={`w-full rounded-xl border bg-white px-4 py-2.5 text-base text-foreground-950 outline-none transition-colors ${avgWorkerSalaryOutOfRange ? 'border-red-400 bg-red-50/40' : 'border-background-200 focus:border-foreground-400'}`}
                      />
                      {avgWorkerSalaryOutOfRange && (
                        <p className="text-red-500 text-xs mt-1.5">Значення поза межами (8 000 – 150 000), застосовано обмеження</p>
                      )}
                    </div>

                    <div>
                      <label htmlFor="calc-days-close" className="block text-foreground-700 text-sm mb-2 font-medium">
                        Скільки днів зараз закриваєте вакансію
                      </label>
                      <input
                        id="calc-days-close"
                        type="number"
                        min={5}
                        max={120}
                        value={calc.daysToClose}
                        onChange={setValue('daysToClose')}
                        className={`w-full rounded-xl border bg-white px-4 py-2.5 text-base text-foreground-950 outline-none transition-colors ${daysToCloseOutOfRange ? 'border-red-400 bg-red-50/40' : 'border-background-200 focus:border-foreground-400'}`}
                      />
                      {daysToCloseOutOfRange && (
                        <p className="text-red-500 text-xs mt-1.5">Значення поза межами (5 – 120), застосовано обмеження</p>
                      )}
                    </div>

                    <div>
                      <SliderRow
                        id="calc-hot-pct"
                        label="Частка вакансій, де місце «горить»"
                        value={calc.hotVacancyPct}
                        min={30}
                        max={100}
                        step={5}
                        unit="%"
                        onChange={setValue('hotVacancyPct')}
                        onMinus={stepValue('hotVacancyPct', -1)}
                        onPlus={stepValue('hotVacancyPct', 1)}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* ── RIGHT: Results ── */}
          <div className="flex flex-col gap-5" aria-live="polite">

            {/* 1. Main result — hero card with accent top border */}
            <div
              className="bg-white rounded-[28px] border border-background-200/70 p-8 lg:p-10 text-center"
              style={{ borderTopWidth: '4px', borderTopColor: BRAND }}
            >
              <div className="text-foreground-500 text-sm mb-3 tracking-wide uppercase" style={{ fontSize: '0.75rem', letterSpacing: '0.06em' }}>
                Зараз це коштує вам приблизно
              </div>
              <div
                className="font-heading font-black mb-3 leading-none tracking-tight"
                style={{ color: BRAND, fontSize: 'clamp(1.75rem, 3.5vw, 2.5rem)' }}
              >
                {fmtMoney(results.totalMin, results.totalMax)} на рік
              </div>
              <div className="text-foreground-500 text-sm">
                = річний ФОП ~{fmtUa(results.fopEq)} лінійних працівників
              </div>
            </div>

            {/* 2. Breakdown card */}
            <div className="bg-white rounded-[28px] border border-background-200/70 p-8 lg:p-10 flex flex-col">
              {/* Row 1: Hard costs */}
              <div className="pb-5 mb-5 border-b border-background-200/60">
                <div className="text-foreground-500 text-sm mb-1">
                  Повільне закриття вакансій
                </div>
                <div className="text-foreground-800 text-base leading-snug mb-2">
                  надвитрати на діри у змінах¹
                </div>
                <div className="font-heading font-bold text-foreground-950 text-lg" style={{ fontSize: '1.25rem' }}>
                  {fmtMoneySingle(results.hardConservative)}–{fmtMoneySingle(results.hardTypical)} / рік
                </div>
              </div>

              {/* Row 2: Soft costs */}
              <div>
                <div className="text-foreground-500 text-sm mb-1">
                  Рутина без автоматизації
                </div>
                <div className="text-foreground-800 text-base leading-snug mb-2">
                  час HR-команди² — ≈ {fmtUa(results.hrHours)} год/міс
                </div>
                <div className="font-heading font-bold text-foreground-950 text-lg mb-2" style={{ fontSize: '1.25rem' }}>
                  еквівалент {fmtMoneySingle(results.soft)} / рік
                </div>
                <span className="inline-block px-3 py-1.5 rounded-lg bg-background-100 text-foreground-500 text-xs leading-snug border border-background-200/70">
                  вивільнений ресурс, не пряме скорочення витрат
                </span>
              </div>
            </div>

            {/* 3. Solution card — dark, stands out */}
            <div
              className="rounded-[28px] p-8 lg:p-10"
              style={{ backgroundColor: DARK_NAVY }}
            >
              <div className="text-white/50 text-xs uppercase tracking-wider mb-3" style={{ fontSize: '0.7rem', letterSpacing: '0.08em' }}>
                З HURMA
              </div>
              <p className="text-white text-base lg:text-lg leading-relaxed">
                найм у <span className="font-bold" style={{ color: BRAND }}>3 рази швидше</span>¹ · мінус <span className="font-bold" style={{ color: BRAND }}>43% рутини</span>² · старт за <span className="font-bold" style={{ color: BRAND }}>10 робочих днів</span>³
              </p>
            </div>

            {/* 4. Fine print */}
            <p className="text-foreground-400 text-xs leading-relaxed px-1">
              Свідомо не рахуємо: плинність новачків, джоб-борди, штрафи за помилки в кадрових документах. Це запас міцності розрахунку.
            </p>

            {/* 5. CTA — full width, prominent */}
            <button
              onClick={() => {
                (window as any).dataLayer?.push?.({ event: 'calc_demo_click' });
                window.location.href = "https://hurma.work/request-2/?utm_source=industry";
              }}
              className="inline-flex w-full items-center justify-center gap-2.5 text-white font-semibold text-base py-4 rounded-full transition-all duration-200 cursor-pointer whitespace-nowrap hover:brightness-110 hover:scale-[1.01]"
              style={{ backgroundColor: BRAND }}
              data-event="calc_demo_click"
            >
              Перевірити цифри на демо
              <i className="ri-arrow-right-line text-base" />
            </button>

            {/* 6. Methodology accordion */}
            <div className="bg-white rounded-[28px] border border-background-200/70 overflow-hidden">
              <button
                onClick={() => {
                  markInteracted();
                  setMethodologyOpen(!methodologyOpen);
                  if (!methodologyOpen) {
                    (window as any).dataLayer?.push?.({ event: 'calc_methodology_open' });
                  }
                }}
                className="w-full flex items-center justify-between gap-3 text-left cursor-pointer px-8 py-5 group"
                data-event="calc_methodology_open"
              >
                <span className="text-foreground-700 text-sm font-medium group-hover:text-foreground-900 transition-colors">
                  Як ми рахуємо
                </span>
                <i
                  className={`ri-arrow-down-s-line text-lg text-foreground-400 transition-transform duration-200 ${methodologyOpen ? 'rotate-180' : ''}`}
                />
              </button>

              {methodologyOpen && (
                <div className="px-8 pb-8 pt-1 text-sm text-foreground-600 leading-relaxed flex flex-col gap-3 border-t border-background-200/60">
                  <p>
                    Діру у зміні закривають понаднормові колег: овертайм за КЗпП оплачується вдвічі, тож кожен день «гарячої» вакансії коштує щонайменше одну денну ставку. Рахуємо лише вакансії-заміни без перекриття ({calc.hotVacancyPct}% наймів) і лише дні, які прибирає прискорення найму втричі (⅔ поточного строку). Час HR: {calc.hrCount} осіб × ЗП × 43% рутини × 12 місяців.
                  </p>
                  <p className="text-foreground-400 text-xs leading-relaxed">
                    ¹ 3× — кейси масового найму клієнтів HURMA · ² 43% — заміри економії часу на клієнтах HURMA · ³ 10 днів — 94% пілотних впроваджень вкладаються у строк.
                  </p>
                  <p className="text-foreground-400 text-xs leading-relaxed">
                    Розрахунок оцінковий і не є фінансовою гарантією чи комерційною пропозицією.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ── Slider row sub-component ── */

interface SliderRowProps {
  id: string;
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  unit: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onMinus: () => void;
  onPlus: () => void;
}

function SliderRow({ id, label, value, min, max, step, unit, onChange, onMinus, onPlus }: SliderRowProps) {
  const pct = ((value - min) / (max - min)) * 100;

  return (
    <div>
      <div className="flex justify-between items-baseline mb-3 gap-4">
        <label htmlFor={id} className="text-foreground-900 text-base font-medium cursor-pointer leading-snug">
          {label}
        </label>
        <span className="font-heading font-bold text-foreground-950 text-base whitespace-nowrap flex-shrink-0">
          {value} {unit}
        </span>
      </div>

      <div className="flex items-center gap-3">
        <button
          onClick={onMinus}
          className="md:hidden w-10 h-10 rounded-full border border-background-200 flex items-center justify-center text-foreground-500 hover:border-foreground-300 hover:text-foreground-700 hover:bg-background-50 transition-colors cursor-pointer flex-shrink-0"
          aria-label="Зменшити"
        >
          <i className="ri-subtract-line text-sm" />
        </button>

        <div className="relative h-5 flex items-center flex-1">
          {/* Custom track */}
          <div className="absolute inset-0 flex items-center pointer-events-none">
            <div className="w-full h-[4px] rounded-full bg-background-200 relative overflow-hidden">
              <div
                className="absolute left-0 top-0 h-full rounded-full"
                style={{ width: `${pct}%`, backgroundColor: BRAND }}
              />
            </div>
          </div>
          {/* Native range input */}
          <input
            id={id}
            type="range"
            min={min}
            max={max}
            step={step}
            value={value}
            onChange={onChange}
            className="calc-slider absolute inset-0 w-full h-full"
          />
        </div>

        <button
          onClick={onPlus}
          className="md:hidden w-10 h-10 rounded-full border border-background-200 flex items-center justify-center text-foreground-500 hover:border-foreground-300 hover:text-foreground-700 hover:bg-background-50 transition-colors cursor-pointer flex-shrink-0"
          aria-label="Збільшити"
        >
          <i className="ri-add-line text-sm" />
        </button>
      </div>
    </div>
  );
}